// ============================================================================
// adminView.js — adminisztrációs panel és profilszerkesztő.
// ============================================================================

import { renderTemakKezelese } from "./adminTemakView.js";
import { renderKozlemenyek } from "./adminKozlemenyView.js";
import { renderKezdolapBeallitas } from "./adminKezdolapView.js";
import { renderKartyaElonezet } from "./adminKartyaView.js";
import { renderJelentkezesekAdmin } from "./adminJelentkezesView.js";
import { renderTagsagiKartya } from "./tagsagiKartya.js";
import { renderNevNaplo } from "./nevNaploView.js";
import { el } from "./dom.js";
import { ENV } from "./env.js";
import { t, localeFor, CHAT_LANGUAGES } from "./i18n.js";
import { getRoomDisplayName } from "./roomName.js";

// Az önkéntes-szobák felismerése az alap név alapján: "Tesztelők" -> tester, "Pénzügyi támogatók" -> admin (jelentkezési szerepek).
export function onkentesSzobaSzerep(name) {
  const n = String(name || "").trim();
  if (/^tesztel(ők|ok|ő|o)(?![\p{L}])/iu.test(n)) return "tester";
  if (/^p[ée]nz[üu]gyi t[áa]mogat/i.test(n)) return "admin";
  if (/^audit(?![\p{L}])/iu.test(n)) return "audit";
  return null;
}
import { szobaFaSorrend, nemValaszthatoSzuloId, szobaOpcioFelirat, alapSorrend, testverCsoport } from "./roomTree.js";
import { javaslatSor, javaslatForras } from "./javaslat.js";
import { renderNevPotlas } from "./nevPotlasView.js";
import { beepitettVerzio, beepitettKiadas } from "./updater.js";

/** A tag felvételének ideje a résztvevő-listákban (v210): "Felvéve: 2026. 11. 02." vagy "nincs adat" a régi tagoknál. */
function felvetelSzoveg(uiLang, user) {
  if (typeof user.addedAt !== "number") return t(uiLang, "admin.userSinceUnknown");
  const datum = new Date(user.addedAt).toLocaleDateString(localeFor(uiLang), { year: "numeric", month: "2-digit", day: "2-digit" });
  return t(uiLang, user.addedApprox === true ? "admin.userSinceApprox" : "admin.userSince", { date: datum });
}


function sheetShell(app, uiLang, title, onClose, teljes = false) {
  const backdrop = el("div", { class: "sheet-backdrop open" });
  // v214: az adminisztráció teljes képernyős (felül ragadós fejléc a címmel és a bezáró gombbal); a profil lap marad felcsúszó panel.
  const sheet = el("div", { class: "sheet tall" + (teljes ? " sheet-full" : "") });
  if (teljes) {
    const fejlec = el("div", { class: "sheet-topbar" });
    fejlec.appendChild(el("h2", { text: title }));
    fejlec.appendChild(el("button", { type: "button", class: "sheet-topbar-close", "data-admin-close": "1", "aria-label": t(uiLang, "admin.close"), text: "✕", onclick: onClose }));
    sheet.appendChild(fejlec);
  } else {
    sheet.appendChild(el("div", { class: "sheet-handle" }));
    sheet.appendChild(el("h2", { text: title }));
  }

  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) onClose();
  });

  backdrop.appendChild(sheet);
  app.appendChild(backdrop);
  return sheet;
}

/**
 * "Szülő szoba (kategória)" választó - a szoba létrehozásánál és
 * szerkesztésénél egyaránt használt <select>. Az `excludeIds` (a szoba
 * saját azonosítója + minden leszármazottja) nem szerepelhet választható
 * lehetőségként, mert az nyilvánvaló kört zárna be - a szerver ezt véglegesen
 * úgyis ellenőrzi, ez csak előre megtisztítja a listát.
 */
function renderSzuloValaszto(uiLang, rooms, ertek, excludeIds, onChange, nevNyelv = null) {
  const box = el("div");
  box.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "admin.roomParentLabel") }));
  const select = el("select", { class: "panel-input" });
  select.appendChild(el("option", { value: "", text: t(uiLang, "admin.roomParentNone") }));
  for (const { room, melyseg } of szobaFaSorrend(rooms)) {
    if (excludeIds && excludeIds.has(room.roomId)) continue;
    if (room.deleting) continue; // törlés alatt álló szoba nem választható szülőnek
    const opt = el("option", {
      value: room.roomId,
      // Az ADMIN nyelvén megjelenő név (2026-10-10), nem az alap név.
      text: szobaOpcioFelirat(melyseg, getRoomDisplayName(room, nevNyelv || uiLang, uiLang)),
    });
    select.appendChild(opt);
  }
  select.value = ertek || "";
  select.addEventListener("change", () => onChange(select.value || null));
  box.appendChild(select);
  return box;
}

/**
 * Egy szoba átnevezésének szerkesztője: alap név + nyelvenkénti nevek.
 * A beviteli mezők értékét a kezelők az állapotba írják (újrarajzolás nélkül,
 * hogy gépelés közben ne vesszen el a fókusz).
 */
function renderRoomEditor(uiLang, room, model, handlers) {
  const { busy, editName, editLocalizedNames, editParentRoomId, contentLangs, rooms } = model;
  const box = el("div", { class: "room-editor" });

  const nameInput = el("input", {
    type: "text",
    class: "panel-input",
    placeholder: t(uiLang, "admin.roomName"),
    maxlength: "160",
  });
  nameInput.value = editName || "";
  nameInput.addEventListener("input", () => handlers.onEditNameChange(nameInput.value));
  nameInput.addEventListener("keydown", (e) => {
    // A japán bevitel (IME) Entere a jelöltválasztás megerősítése, nem mentés.
    if (e.isComposing || e.keyCode === 229) return;
    if (e.key === "Enter") handlers.onSaveRename();
    if (e.key === "Escape") handlers.onCancelRename();
  });
  box.appendChild(nameInput);
  // Magyarázat (2026-10-10): a megjelenített név a nyelvenkénti név; az alap név átírásakor a változatlan
  // nyelvi nevek elavultak, ezért törlődnek, és ott az új alap név látszik.
  box.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.renameStaleHint") }));
  // Már elrontott szobákhoz (az alap név az új, a nyelvi nevek viszont régiek): egy kattintással törölhetők.
  box.appendChild(el("button", {
    type: "button", class: "text-btn clear-localized-btn", text: t(uiLang, "admin.clearLocalizedNames"),
    disabled: busy, onclick: () => handlers.onClearLocalizedNames && handlers.onClearLocalizedNames(),
  }));

  const localized = editLocalizedNames || {};
  const szerkesztoInputok = {};
  for (const [code, label] of Object.entries(contentLangs || {})) {
    const langName = typeof label === "object" && label !== null ? (label.name || code) : label;
    box.appendChild(
      el("label", { class: "panel-label small", text: t(uiLang, "admin.roomNameLang", { lang: langName }) })
    );
    const input = el("input", {
      type: "text",
      class: "panel-input",
      placeholder: t(uiLang, "admin.roomNameLangHint"),
      maxlength: "160",
    });
    input.value = localized[code] || "";
    input.addEventListener("input", () => handlers.onEditLocalizedNameChange(code, input.value));
    szerkesztoInputok[code] = input;
    box.appendChild(input);
  }

  // Javaslat (AI) a nyelvenkénti nevekhez (2026-10-04). A beviteli mezők értékét
  // a kezelők újrarajzolás nélkül írják az állapotba, ezért a javaslatot
  // KÖZVETLENÜL a mezőbe is be kell írni (különben nem látszana).
  if (handlers.onSuggestRoomNames && Object.keys(szerkesztoInputok).length > 0) {
    const roomId = room.roomId;
    box.appendChild(
      javaslatSor({
        uiLang,
        gyujt: () => ({
          ...javaslatForras(nameInput.value, Object.entries(szerkesztoInputok).map(([kod, inp]) => ({ kod, ertek: inp.value }))),
          mezok: Object.entries(szerkesztoInputok).map(([kod, inp]) => ({ kod, ures: !inp.value.trim() })),
        }),
        kerj: (alap, kodok, alapNyelv) => handlers.onSuggestRoomNames(alap, kodok, alapNyelv),
        alkalmaz: (kod, ertek) => {
          const inp = szerkesztoInputok[kod];
          if (!inp || inp.value.trim()) return false; // közben beírt valamit: nem írjuk felül
          inp.value = ertek;
          handlers.onEditLocalizedNameChange(kod, ertek);
          return true;
        },
        ervenyes: () => (handlers.isEditingRoom ? handlers.isEditingRoom(roomId) : true),
      }).elem
    );
  }

  box.appendChild(
    renderSzuloValaszto(
      uiLang,
      rooms || [],
      editParentRoomId,
      nemValaszthatoSzuloId(rooms || [], room.roomId),
      handlers.onEditParentChange,
      model.contentLang || uiLang
    )
  );

  const row = el("div", { class: "panel-row" });
  row.appendChild(
    el("button", {
      type: "button",
      class: "primary-btn compact",
      text: t(uiLang, "admin.renameSave"),
      disabled: busy,
      onclick: handlers.onSaveRename,
    })
  );
  row.appendChild(
    el("button", {
      type: "button",
      class: "neutral-btn",
      text: t(uiLang, "admin.cancel"),
      disabled: busy,
      onclick: handlers.onCancelRename,
    })
  );
  box.appendChild(row);
  return box;
}

/**
 * Az admin panel almenüi. A címkék MEGLÉVŐ feliratkulcsok (mind a 15 nyelven
 * le vannak fordítva), így az új menü nem igényel új fordítást.
 *   rooms    - szobák létrehozása, átnevezése, törlése
 *   newUser  - új résztvevő felvétele
 *   users    - résztvevők listája, eltávolítás
 *   members  - melyik szobának kik a tagjai
 *   usage    - API-költség / napi keret kimutatás
 */
export const ADMIN_MENUS = [
  { key: "rooms", labelKey: "admin.manageRooms" },
  { key: "newUser", labelKey: "admin.addUser" },
  { key: "users", labelKey: "admin.users" },
  { key: "topics", labelKey: "admin.manageTopics" },
  { key: "announcements", labelKey: "admin.announcements" },
  { key: "home", labelKey: "admin.homeMenu" },
  { key: "card", labelKey: "admin.cardMenu" },
  { key: "applications", labelKey: "admin.applicationsMenu" },
  { key: "usage", labelKey: "admin.usageTitle" },
];

/**
 * A "Szobák kezelése" almenüi (2026-10-20, v122; Tamás: "a szoba menü legyen almenükre bontva: 1. létrehozás, 2. módosítás (név, törlés,
 * átstrukturálás), 3. a szoba tagjai"). A főmenüből a "rooms" nyitja; a gyerek-kulcsok önálló oldalak.
 */
export const ADMIN_SUBMENUS = {
  rooms: [
    { key: "roomNew", labelKey: "admin.createRoom" },
    { key: "roomEdit", labelKey: "admin.modifyRooms" },
    { key: "members", labelKey: "admin.roomMembers" },
  ],
};

/** A gyerek-oldal szülő-menüje (null, ha főmenü-szintű). */
export function adminSzuloMenu(key) {
  for (const [szulo, gyerekek] of Object.entries(ADMIN_SUBMENUS)) if (gyerekek.some((g) => g.key === key)) return szulo;
  return null;
}

// Témafelelős (2026-09-30, tagi kérésre): "az admin alatt helyezkedik el" -
// csak a szobakezelést és a szobatagságot (kinek van jogosultsága
// megtekinteni a szoba tartalmát) éri el. A felhasználók kezelése (felvétel,
// eltávolítás, szerepkör) és a költségadat admin-only marad. A szerver
// (adminAction.js TOPIC_LEAD_WRITE_ACTIONS) ugyanezt kényszeríti ki - ez a
// szűrés csak a felületet tisztítja, nem helyettesíti azt.
const TOPIC_LEAD_MENU_KEYS = new Set(["rooms"]);

/** Adminisztrációs panel: főmenü + almenük (szobák, résztvevők, tagság, költség). */
// A "Szoba módosítása" lista lenyitott szobái (v142, Tamás kérése): a szoba részletei alapból összecsukva állnak, a nyíllal nyílnak. A halmaz
// modulszintű, így a lista újrarajzolása (pl. ▲▼ sorrendváltás, modellváltás) után a lenyitott szoba nyitva marad.
const nyitottSzobak = new Set();
// A profil "Név más írásrendszerekkel" része nyitva van-e (v174): modulszintű, így a profil újrarajzolása (pl. a "Javaslat" után) nem csukja be.
let nevValtozatokNyitva = false;

export function renderAdminPanel(app, model, handlers) {
  const { uiLang, rooms, users, selectedRoomId, newRoomName, busy, statusKey, errorKey, editingRoomId, contentLangs } = model;
  const isTopicLeadCaller = model.callerRole === "topic_lead";
  const visibleMenus = isTopicLeadCaller
    ? ADMIN_MENUS.filter((m) => TOPIC_LEAD_MENU_KEYS.has(m.key))
    : ADMIN_MENUS;
  const lathatoKulcsok = new Set(visibleMenus.map((m) => m.key));
  for (const m of visibleMenus) for (const g of ADMIN_SUBMENUS[m.key] || []) lathatoKulcsok.add(g.key);
  const menu = lathatoKulcsok.has(model.menu) ? model.menu : null;
  const sheet = sheetShell(app, uiLang, t(uiLang, "admin.title"), handlers.onClose, true);

  // Friss belépés kell (v135/v136): a lap TETEJÉN, jól látható keretben (élő tapasztalat: a lap alján, a hosszú lista alatt nem látszott, és
  // egy újrarajzolás elvitte). A gomb megismétli a Google-belépést, és folytatja a megszakadt műveletet (az adat megmarad). Addig látszik,
  // amíg a folytatás függőben van - nem függ a hibasortól.
  if (model.reauth && handlers.onReauth) {
    const doboz = el("div", { class: "panel-status err reauth-box", role: "alert" });
    const cimke = model.reauthLabel && model.reauthLabel.action ? t(uiLang, "admin.reauthAct." + model.reauthLabel.action) : "";
    const nev = (model.reauthLabel && model.reauthLabel.name) || "";
    if (model.reauthMode === "megerosit") {
      // Lapújratöltés utáni folytatás (titkár #65): a belépés megtörtént, de a műveletet újra meg kell mutatni, és külön jóváhagyást kér (nem fut le magától).
      doboz.appendChild(el("p", { text: t(uiLang, "admin.reauthResumeAsk", { action: cimke, name: nev }) }));
      doboz.appendChild(el("button", { type: "button", class: "primary-btn reauth-btn", disabled: busy, text: t(uiLang, "admin.reauthResumeYes"), onclick: handlers.onReauthContinue }));
      doboz.appendChild(el("button", { type: "button", class: "text-btn reauth-cancel", disabled: busy, text: t(uiLang, "admin.reauthResumeNo"), onclick: handlers.onReauthCancel }));
    } else {
      doboz.appendChild(el("p", { text: t(uiLang, "admin.reauthNeeded") }));
      if (cimke) doboz.appendChild(el("p", { class: "reauth-pending", text: t(uiLang, "admin.reauthPending", { action: cimke, name: nev }) }));
      if (errorKey === "admin.reauthFailed") doboz.appendChild(el("p", { text: t(uiLang, "admin.reauthFailed") }));
      doboz.appendChild(el("button", { type: "button", class: "primary-btn reauth-btn", disabled: busy, text: t(uiLang, "admin.reauthButton"), onclick: handlers.onReauth }));
    }
    sheet.appendChild(doboz);
  }

  // Az egyes almenük tartalma; a végén csak a kiválasztott kerül a lapra.
  const parts = { rooms: [], roomNew: [], roomEdit: [], newUser: [], users: [], members: [], topics: [], announcements: [], home: [], card: [], applications: [], usage: [] };

  // --- Szoba létrehozása
  const createSection = el("section", { class: "panel-section" });
  createSection.appendChild(el("h3", { text: t(uiLang, "admin.createRoom") }));

  // Alap név (kötelező)
  const nameInput = el("input", {
    type: "text",
    class: "panel-input",
    placeholder: t(uiLang, "admin.roomName"),
    maxlength: "160",
  });
  nameInput.value = newRoomName || "";
  nameInput.addEventListener("input", () => handlers.onNewRoomNameChange(nameInput.value));
  nameInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") handlers.onCreateRoom();
  });
  createSection.appendChild(nameInput);

  // Lokalizált nevek (opcionális, minden konfigurált nyelvhez)
  const localizedNames = model.localizedNewRoomNames || {};
  const letrehozoInputok = {};
  for (const [code, label] of Object.entries(model.contentLangs || {})) {
    const langName = typeof label === "object" && label !== null ? (label.name || code) : label;
    createSection.appendChild(
      el("label", { class: "panel-label small", text: t(uiLang, "admin.roomNameLang", { lang: langName }) })
    );
    const localInput = el("input", {
      type: "text",
      class: "panel-input",
      placeholder: t(uiLang, "admin.roomNameLangHint"),
      maxlength: "160",
    });
    localInput.value = localizedNames[code] || "";
    localInput.addEventListener("input", () => handlers.onLocalizedNameChange(code, localInput.value));
    letrehozoInputok[code] = localInput;
    createSection.appendChild(localInput);
  }

  // Javaslat (AI) az új szoba nyelvenkénti neveihez (2026-10-04).
  if (handlers.onSuggestRoomNames && Object.keys(letrehozoInputok).length > 0) {
    createSection.appendChild(
      javaslatSor({
        uiLang,
        gyujt: () => ({
          ...javaslatForras(nameInput.value, Object.entries(letrehozoInputok).map(([kod, inp]) => ({ kod, ertek: inp.value }))),
          mezok: Object.entries(letrehozoInputok).map(([kod, inp]) => ({ kod, ures: !inp.value.trim() })),
        }),
        kerj: (alap, kodok, alapNyelv) => handlers.onSuggestRoomNames(alap, kodok, alapNyelv),
        alkalmaz: (kod, ertek) => {
          const inp = letrehozoInputok[kod];
          if (!inp || inp.value.trim()) return false;
          inp.value = ertek;
          handlers.onLocalizedNameChange(kod, ertek);
          return true;
        },
      }).elem
    );
  }

  createSection.appendChild(
    renderSzuloValaszto(uiLang, rooms || [], model.newRoomParentId, null, handlers.onNewRoomParentChange, model.contentLang || uiLang)
  );

  // Csak olvasható szoba (2026-10-08, tagi kérésre): a tagok csak olvasnak, admin írhat. CSAK adminnak
  // látszik - a szerver a témafelelőstől el is utasítaná (abban a szobában ő maga sem írhatna).
  if (!isTopicLeadCaller && handlers.onNewRoomReadOnlyChange) {
    const jelolo = el("input", { type: "checkbox", id: "new-room-readonly" });
    jelolo.checked = model.newRoomReadOnly === true;
    jelolo.addEventListener("change", () => handlers.onNewRoomReadOnlyChange(jelolo.checked));
    const sor = el("label", { class: "panel-label small readonly-check", for: "new-room-readonly" });
    sor.appendChild(jelolo);
    sor.appendChild(document.createTextNode(" " + t(uiLang, "admin.readOnlyRoom")));
    createSection.appendChild(sor);
  }

  const createRow = el("div", { class: "panel-row" });
  createRow.appendChild(el("div")); // spacer
  createRow.appendChild(
    el("button", {
      type: "button",
      class: "primary-btn compact",
      text: busy ? t(uiLang, "admin.creating") : t(uiLang, "admin.create"),
      disabled: busy,
      onclick: handlers.onCreateRoom,
    })
  );
  createSection.appendChild(createRow);
  parts.roomNew.push(createSection);

  // --- Szobák kezelése: átnevezés és törlés
  const manageSection = el("section", { class: "panel-section" });
  manageSection.appendChild(el("h3", { text: t(uiLang, "admin.modifyRooms") }));

  if (!rooms) {
    manageSection.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
  } else if (rooms.length === 0) {
    manageSection.appendChild(el("p", { class: "hint", text: t(uiLang, "chat.noRoomsAdmin") }));
  }

  const roomListBox = el("div", { class: "panel-list" });
  // A lista a tagok által látott (élő) sorrendben indul, majd az admin által beállított sorrend szerint (v125).
  const rendezettSzobak = alapSorrend(rooms || [], model.eloSzobak || []);
  for (const { room, melyseg } of szobaFaSorrend(rendezettSzobak)) {
    if (room.roomId === editingRoomId) {
      const szerkeszto = renderRoomEditor(uiLang, room, model, handlers);
      szerkeszto.style.marginLeft = melyseg * 18 + "px";
      roomListBox.appendChild(szerkeszto);
      continue;
    }

    // room-manage-row (2026-10-10): a név teljes szélességben, törhető sorokban áll, a műveletek ALATTA
    // (mobilon a keskeny sorban a név "Ha…"-ra csonkult és olvashatatlan volt). A név az ADMIN nyelvén
    // jelenik meg (nyelvenkénti név, annak hiányában az alap név), nem mindig az alap név.
    const row = el("div", { class: "user-row room-manage-row" });
    row.style.marginLeft = melyseg * 18 + "px";
    const textBox = el("div", { class: "user-row-text" });
    // Az összecsukott sorban csak a név és a fontos jelzések látszanak (csak olvasható / útmutató / törlés folyamatban); a többi a nyíllal nyílik.
    const nevElem = el("div", {
      class: "primary",
      text: (melyseg > 0 ? "\u21B3 " : "") + getRoomDisplayName(room, model.contentLang || uiLang, uiLang),
    });
    const jelzesek = [];
    // A ténylegesen fordított nyelvek - ez dönti el, mire megy API-hívás.
    // `null`: még nincs kiszámolva (a szerver az első üzenetkor pótolja).
    const langNames = (room.activeLangs || []).map((code) => {
      const label = contentLangs && contentLangs[code];
      return (typeof label === "object" && label !== null ? label.name : label) || code;
    });
    textBox.appendChild(
      el("div", {
        class: "sub",
        text: room.activeLangs === null
          ? t(uiLang, "admin.roomLangsPending")
          : t(uiLang, "admin.roomLangs", { langs: langNames.length ? langNames.join(", ") : t(uiLang, "admin.roomLangsNone") }),
      })
    );
    // Csak olvasható szoba jelölése (2026-10-08).
    if (room.readOnly === true) {
      jelzesek.push(el("div", { class: "sub", text: t(uiLang, "admin.readOnlyBadge") }));
    }
    // Fordító modell szobánként (2026-10-10, csak admin): Flash (alap) vagy Lite (olcsóbb, gyengébb minőség).
    if (!room.deleting && !isTopicLeadCaller && handlers.onSetRoomTranslationModel) {
      const modellSor = el("label", { class: "room-model-row" });
      modellSor.appendChild(el("span", { class: "sub", text: t(uiLang, "admin.translationModel") }));
      const valaszto = el("select", { class: "panel-input room-model-select", disabled: busy });
      for (const [ertek, kulcs] of [["flash", "admin.modelFlash"], ["lite", "admin.modelLite"]]) {
        const o = el("option", { value: ertek, text: t(uiLang, kulcs) });
        if ((room.translationModel === "lite" ? "lite" : "flash") === ertek) o.selected = true;
        valaszto.appendChild(o);
      }
      valaszto.addEventListener("change", () => handlers.onSetRoomTranslationModel(room, valaszto.value));
      modellSor.appendChild(valaszto);
      textBox.appendChild(modellSor);
    }
    // Offline olvasás szobánként (v143, csak admin): alapból kikapcsolva; bekapcsolva a tag saját eszközén mentődik a szoba legutóbbi tartalma.
    if (!room.deleting && !isTopicLeadCaller && handlers.onSetRoomOfflineRead) {
      const offlineDoboz = el("div", { class: "room-offline-box" });
      const offlineJelolo = el("input", { type: "checkbox", disabled: busy });
      offlineJelolo.checked = room.offlineOlvasas === true;
      offlineJelolo.addEventListener("change", () => handlers.onSetRoomOfflineRead(room, offlineJelolo.checked));
      offlineDoboz.appendChild(el("label", { class: "room-guide-row" }, [offlineJelolo, el("span", { text: " " + t(uiLang, "admin.offlineRead") })]));
      offlineDoboz.appendChild(el("div", { class: "sub", text: t(uiLang, "admin.offlineReadHint") }));
      textBox.appendChild(offlineDoboz);
    }
    // v215 (csak admin): a szoba régi üzeneteinek pótló fordítása az utólag csatlakozott nyelvekre (pl. új, francia tag).
    if (!room.deleting && !room.archived && !isTopicLeadCaller && handlers.onBackfillTranslations) {
      const potlasDoboz = el("div", { class: "room-backfill-box", "data-room-backfill": room.roomId });
      const atiratJelolo = el("input", { type: "checkbox", disabled: busy, "data-backfill-transcripts": "1" });
      potlasDoboz.appendChild(el("label", { class: "room-guide-row" }, [atiratJelolo, el("span", { text: " " + t(uiLang, "admin.backfillTranscripts") })]));
      const eredmeny = model.potlas && model.potlas[room.roomId];
      const kovetkezo = eredmeny && eredmeny.next != null ? eredmeny.next : null;
      potlasDoboz.appendChild(el("button", {
        type: "button", class: "secondary-btn compact", disabled: busy, "data-backfill-btn": room.roomId,
        text: t(uiLang, kovetkezo != null ? "admin.backfillMore" : "admin.backfillBtn"),
        onclick: () => handlers.onBackfillTranslations(room, { transcripts: atiratJelolo.checked, after: kovetkezo }),
      }));
      potlasDoboz.appendChild(el("div", { class: "sub", text: t(uiLang, "admin.backfillHint") }));
      if (eredmeny) {
        const szoveg = eredmeny.failed ? t(uiLang, "admin.backfillFailed")
          : eredmeny.started > 0 ? t(uiLang, "admin.backfillStarted", { n: eredmeny.started })
          : t(uiLang, "admin.backfillNone");
        potlasDoboz.appendChild(el("div", { class: "sub backfill-result", role: "status", text: szoveg }));
      }
      textBox.appendChild(potlasDoboz);
    }
    // Útmutató-szoba (2026-10-19, a titkár #35, csak admin): a szoba csak bejárat a gyerekszobákhoz, új téma nem nyitható benne;
    // a leírás nyelvenként (hu/en/ja). A mezők mentésig a böngészőben maradnak (nincs újrarajzolás gépelés közben).
    if (!room.deleting && !isTopicLeadCaller && handlers.onSetRoomGuide) {
      const doboz = el("div", { class: "room-guide-box" });
      const jelolo = el("input", { type: "checkbox", disabled: busy });
      jelolo.checked = room.guide === true;
      doboz.appendChild(el("label", { class: "room-guide-row" }, [jelolo, el("span", { text: " " + t(uiLang, "admin.guideRoom") })]));
      doboz.appendChild(el("div", { class: "sub", text: t(uiLang, "admin.guideHint") }));
      // A leírás minden csevegési nyelven megadható: hu / en / ja közvetlenül látszik, a többi összecsukva, és a hiányzókra gépi javaslat kérhető
      // (v121; a javaslat csak a MÉG ÜRES mezőket tölti ki, az admin átnézi, és a mentés gombbal menti).
      const mezok = {};
      const mezoKeszit = (nyelv) => {
        const mezo = el("textarea", { class: "panel-input room-guide-text", rows: "2", maxlength: "500", lang: nyelv, "aria-label": t(uiLang, "admin.guideTextLabel", { lang: nyelv }), placeholder: t(uiLang, "admin.guideTextLabel", { lang: nyelv }) });
        mezo.value = (room.guideText && room.guideText[nyelv]) || "";
        mezok[nyelv] = mezo;
        return mezo;
      };
      const foNyelvek = ["hu", "en", "ja"];
      for (const nyelv of foNyelvek) doboz.appendChild(mezoKeszit(nyelv));
      const tobbiNyelv = Object.keys(CHAT_LANGUAGES).filter((k) => !foNyelvek.includes(k));
      if (tobbiNyelv.length > 0) {
        const tobbi = el("details", { class: "room-guide-more" }, [el("summary", { text: t(uiLang, "admin.guideMore", { n: tobbiNyelv.length }) })]);
        for (const nyelv of tobbiNyelv) tobbi.appendChild(mezoKeszit(nyelv));
        doboz.appendChild(tobbi);
      }
      if (handlers.onSuggestGuideText) {
        const allapot = el("div", { class: "sub", role: "status" });
        const javasol = el("button", {
          type: "button", class: "neutral-btn", text: t(uiLang, "admin.guideSuggest"), disabled: busy,
          onclick: async () => {
            const forras = ["hu", "en", "ja"].find((k) => mezok[k] && mezok[k].value.trim());
            const celok = Object.keys(mezok).filter((k) => !mezok[k].value.trim());
            if (!forras) { allapot.textContent = t(uiLang, "admin.guideSuggestNoSource"); return; }
            if (celok.length === 0) { allapot.textContent = t(uiLang, "admin.guideSuggestNone"); return; }
            javasol.disabled = true;
            allapot.textContent = "…";
            try {
              const javaslat = await handlers.onSuggestGuideText(mezok[forras].value.trim(), celok, forras);
              let db = 0;
              for (const [k, v] of Object.entries(javaslat || {})) if (mezok[k] && !mezok[k].value.trim() && typeof v === "string" && v.trim()) { mezok[k].value = v; db++; }
              allapot.textContent = db > 0 ? t(uiLang, "admin.guideSuggestDone", { n: db }) : t(uiLang, "admin.guideSuggestFailed");
            } catch (err) {
              allapot.textContent = t(uiLang, "admin.guideSuggestFailed");
            }
            javasol.disabled = false;
          },
        });
        doboz.appendChild(javasol);
        doboz.appendChild(allapot);
      }
      doboz.appendChild(el("button", {
        type: "button", class: "neutral-btn", text: t(uiLang, "admin.guideSave"), disabled: busy,
        onclick: () => handlers.onSetRoomGuide(room, jelolo.checked, Object.fromEntries(Object.entries(mezok).map(([k, m]) => [k, m.value]))),
      }));
      textBox.appendChild(doboz);
    }
    if (room.guide === true) jelzesek.push(el("div", { class: "sub", text: t(uiLang, "admin.guideBadge") }));
    if (room.offlineOlvasas === true) jelzesek.push(el("div", { class: "sub", text: t(uiLang, "admin.offlineReadBadge") }));
    // A megszakadt törlésű szoba jelölést kap: az admin lássa, hogy újra kell próbálnia.
    if (room.deleting) {
      jelzesek.push(el("div", { class: "sub warn", text: t(uiLang, "admin.roomDeletePending") }));
    }

    const actions = el("div", { class: "row-actions" });
    // Sorrend (v125, Mamoru #52; csak admin): a szoba a testvérei között fel / le. Másik szülő alá tett szoba az új csoport végére kerül.
    if (!room.deleting && !isTopicLeadCaller && handlers.onMoveRoom) {
      const { testverek, index } = testverCsoport(rendezettSzobak, room);
      if (testverek.length > 1) {
        actions.appendChild(el("button", { type: "button", class: "neutral-btn compact room-move-up", text: "▲", title: t(uiLang, "admin.moveUp"), "aria-label": t(uiLang, "admin.moveUp"), disabled: busy || index <= 0, onclick: () => handlers.onMoveRoom(room, -1) }));
        actions.appendChild(el("button", { type: "button", class: "neutral-btn compact room-move-down", text: "▼", title: t(uiLang, "admin.moveDown"), "aria-label": t(uiLang, "admin.moveDown"), disabled: busy || index < 0 || index >= testverek.length - 1, onclick: () => handlers.onMoveRoom(room, 1) }));
      }
    }
    // Az átnevezés (2026-09-30, tagi kérésre) Témafelelősnek is elérhető -
    // a szerver ugyanezt engedi (TOPIC_LEAD_WRITE_ACTIONS).
    if (!room.deleting) {
      actions.appendChild(
        el("button", {
          type: "button",
          class: "neutral-btn",
          text: t(uiLang, "admin.rename"),
          disabled: busy,
          onclick: () => handlers.onStartRename(room),
        })
      );
    }
    // Csak olvasható be/ki (2026-10-08) - csak adminnak (a szerver is csak adminnak engedi).
    if (!room.deleting && !isTopicLeadCaller && handlers.onToggleRoomReadOnly) {
      actions.appendChild(
        el("button", {
          type: "button",
          class: "neutral-btn",
          text: t(uiLang, room.readOnly === true ? "admin.readOnlyOff" : "admin.readOnlyOn"),
          disabled: busy,
          onclick: () => handlers.onToggleRoomReadOnly(room),
        })
      );
    }
    actions.appendChild(
      el("button", {
        type: "button",
        class: "danger-btn",
        text: t(uiLang, "admin.deleteRoom"),
        disabled: busy,
        onclick: () => handlers.onDeleteRoom(room),
      })
    );
    // Összecsukható sor (natív <details>): a nyíl alatt a nyelvek, a fordító modell, az útmutató-beállítások és a műveletek.
    const reszletek = el("details", { class: "room-details" });
    if (nyitottSzobak.has(room.roomId) || room.deleting) reszletek.open = true; // a megszakadt törlésű szoba nyitva áll: ott a teendő
    reszletek.addEventListener("toggle", () => {
      if (reszletek.open) nyitottSzobak.add(room.roomId);
      else nyitottSzobak.delete(room.roomId);
    });
    const fej = el("div", { class: "user-row-text" });
    fej.appendChild(nevElem);
    for (const j of jelzesek) fej.appendChild(j);
    reszletek.appendChild(el("summary", { class: "room-summary" }, [fej]));
    const torzs = el("div", { class: "room-details-body" });
    torzs.appendChild(textBox);
    torzs.appendChild(actions);
    reszletek.appendChild(torzs);
    row.appendChild(reszletek);
    roomListBox.appendChild(row);
  }
  manageSection.appendChild(roomListBox);
  parts.roomEdit.push(manageSection);
  // Hiányzó nyelvi nevek tömeges pótlása (v123): csak admin (a címkekatalógus mentése admin-művelet).
  if (!isTopicLeadCaller) parts.roomEdit.push(renderNevPotlas({ ...model, eloSzobak: model.eloSzobak || [] }, handlers));


  // --- Új résztvevő felvétele
  const newUserSection = el("section", { class: "panel-section" });
  newUserSection.appendChild(el("h3", { text: t(uiLang, "admin.addUser") }));

  // Felvétel űrlap
  newUserSection.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "admin.userEmail") }));
  const emailInput = el("input", {
    type: "email",
    class: "panel-input",
    placeholder: "name@gmail.com",
    maxlength: "254",
  });
  emailInput.value = model.newUserEmail || "";
  emailInput.addEventListener("input", () => handlers.onNewUserEmailChange(emailInput.value));
  newUserSection.appendChild(emailInput);

  newUserSection.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "admin.userName") }));
  const userNameInput = el("input", {
    type: "text",
    class: "panel-input",
    maxlength: "80",
  });
  userNameInput.value = model.newUserName || "";
  userNameInput.addEventListener("input", () => handlers.onNewUserNameChange(userNameInput.value));
  newUserSection.appendChild(userNameInput);

  // v207: szobák, amelyekbe az új tag azonnal bekerül (pl. Tesztelők / Pénzügyi támogatók).
  if ((rooms || []).length) {
    newUserSection.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "admin.userRooms") }));
    const kijelolt = new Set(model.newUserRooms || []);
    const doboz = el("div", { class: "new-user-rooms" });
    const sor = (room) => {
      const cb = el("input", { type: "checkbox", class: "new-user-room-cb", "data-room-id": room.roomId });
      cb.checked = kijelolt.has(room.roomId);
      cb.addEventListener("change", () => handlers.onNewUserRoomToggle(room.roomId, cb.checked));
      return el("label", { class: "new-user-room" }, [cb, el("span", { text: getRoomDisplayName(room, model.contentLang || uiLang) })]);
    };
    // Elöl az önkéntes-szobák (Audit, Tesztelők, Pénzügyi támogatók), elválasztóval a többitől.
    const elol = rooms.filter((r) => onkentesSzobaSzerep(r.name));
    const sorrend = { audit: 0, tester: 1, admin: 2 };
    elol.sort((a, b) => sorrend[onkentesSzobaSzerep(a.name)] - sorrend[onkentesSzobaSzerep(b.name)]);
    for (const room of elol) doboz.appendChild(sor(room));
    if (elol.length) doboz.appendChild(el("hr", { class: "new-user-rooms-sep" }));
    for (const room of rooms) if (!onkentesSzobaSzerep(room.name)) doboz.appendChild(sor(room));
    newUserSection.appendChild(doboz);
  }

  const addRow = el("div", { class: "panel-row" });
  addRow.appendChild(el("div"));
  addRow.appendChild(
    el("button", {
      type: "button",
      class: "primary-btn compact",
      text: busy ? t(uiLang, "admin.adding") : t(uiLang, "admin.add"),
      disabled: busy,
      onclick: handlers.onAddUser,
    })
  );
  newUserSection.appendChild(addRow);
  parts.newUser.push(newUserSection);

  // --- Meglévő résztvevők listája (eltávolítás)
  const usersSection = el("section", { class: "panel-section" });
  usersSection.appendChild(el("h3", { text: t(uiLang, "admin.users") }));
  const userListBox = el("div", { class: "panel-list" });
  if (!users) {
    userListBox.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
  } else {
    // v212: választható rendezés (név / felvétel ideje), mindkettő növekvő; a felvétel nélküliek a végére kerülnek.
    const rendezes = model.userSort === "added" ? "added" : "name";
    const nev = (u) => String(u.name || u.displayName || u.email || u.uid);
    const aktivak = users.filter((u) => u.active).sort((a, b) => {
      if (rendezes === "added") {
        const x = typeof a.addedAt === "number" ? a.addedAt : Infinity;
        const y = typeof b.addedAt === "number" ? b.addedAt : Infinity;
        if (x !== y) return x === Infinity ? 1 : y === Infinity ? -1 : x - y;
      }
      return nev(a).localeCompare(nev(b), uiLang, { sensitivity: "base" });
    });
    if (aktivak.length > 1) {
      const sorRend = el("div", { class: "user-sort-row" });
      sorRend.appendChild(el("span", { class: "hint", text: t(uiLang, "admin.sortBy") }));
      for (const [kulcs, felirat] of [["name", "admin.sortName"], ["added", "admin.sortAdded"]]) {
        sorRend.appendChild(el("button", {
          type: "button",
          class: "secondary-btn compact" + (rendezes === kulcs ? " active" : ""),
          "data-user-sort": kulcs,
          "aria-pressed": rendezes === kulcs ? "true" : "false",
          text: t(uiLang, felirat),
          onclick: () => handlers.onUserSort(kulcs),
        }));
      }
      userListBox.appendChild(sorRend);
    }
    // v216: e-mail a kijelölt tagoknak (csak admin; a szerver is csak adminnak engedi). A lista sorai jelölőnégyzetet kapnak.
    const emailAllapot = model.email || null;
    const emailNyitva = Boolean(emailAllapot && emailAllapot.open) && !isTopicLeadCaller && Boolean(handlers.onEmailToggle);
    const emailKijelolt = new Set(emailAllapot && Array.isArray(emailAllapot.selected) ? emailAllapot.selected : []);
    if (aktivak.length > 0 && !isTopicLeadCaller && handlers.onEmailToggle) {
      const emailSor = el("div", { class: "user-sort-row" });
      emailSor.appendChild(el("button", {
        type: "button", class: "secondary-btn compact" + (emailNyitva ? " active" : ""), "data-email-toggle": "1",
        "aria-pressed": emailNyitva ? "true" : "false", text: t(uiLang, "admin.emailBtn"), onclick: () => handlers.onEmailToggle(),
      }));
      if (handlers.onSyncMemberNames) {
        emailSor.appendChild(el("button", {
          type: "button", class: "secondary-btn compact", "data-name-sync": "1", disabled: busy,
          text: t(uiLang, "admin.nameSyncBtn"), onclick: () => handlers.onSyncMemberNames(),
        }));
      }
      userListBox.appendChild(emailSor);
    }
    if (emailNyitva) {
      const urlap = el("div", { class: "email-box", "data-email-box": "1" });
      const vezerlo = el("div", { class: "user-sort-row" });
      vezerlo.appendChild(el("button", { type: "button", class: "secondary-btn compact", "data-email-all": "1", text: t(uiLang, "admin.emailSelectAll"), onclick: () => handlers.onEmailSelectAll(aktivak.map((u) => u.uid)) }));
      vezerlo.appendChild(el("button", { type: "button", class: "secondary-btn compact", "data-email-none": "1", text: t(uiLang, "admin.emailSelectNone"), onclick: () => handlers.onEmailSelectNone() }));
      vezerlo.appendChild(el("span", { class: "hint", "data-email-count": "1", text: t(uiLang, "admin.emailSelected", { n: emailKijelolt.size }) }));
      urlap.appendChild(vezerlo);
      urlap.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "admin.emailSubject") }));
      urlap.appendChild(el("input", {
        type: "text", class: "panel-input", maxlength: "150", "data-email-subject": "1", value: (emailAllapot && emailAllapot.subject) || "", disabled: busy,
        oninput: (e) => handlers.onEmailDraft("subject", e.target.value),
      }));
      urlap.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "admin.emailBody") }));
      const szovegmezo = el("textarea", {
        class: "panel-input email-body", rows: "8", maxlength: "5000", "data-email-body": "1", disabled: busy,
        oninput: (e) => handlers.onEmailDraft("body", e.target.value),
      });
      szovegmezo.value = (emailAllapot && emailAllapot.body) || "";
      urlap.appendChild(szovegmezo);
      urlap.appendChild(el("button", { type: "button", class: "primary-btn compact", "data-email-send": "1", disabled: busy, text: t(uiLang, "admin.emailSend"), onclick: () => handlers.onEmailSend() }));
      if (emailAllapot && emailAllapot.errorKey) urlap.appendChild(el("p", { class: "panel-status err", role: "alert", text: t(uiLang, emailAllapot.errorKey) }));
      if (emailAllapot && emailAllapot.result) {
        urlap.appendChild(el("p", { class: "panel-status ok", role: "status", "data-email-result": "1", text: t(uiLang, "admin.emailSent", { n: emailAllapot.result.queued }) + (emailAllapot.result.skipped > 0 ? " " + t(uiLang, "admin.emailSkipped", { n: emailAllapot.result.skipped }) : "") }));
      }
      userListBox.appendChild(urlap);
    }
    if (aktivak.length === 0) {
      userListBox.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.noUsers") }));
    }
    for (const user of aktivak) {
      // user-manage-row (2026-10-10, mobil hiba): a név teljes szélességben, törhetően áll, a szerepkör-gombok ALATTA -
      // korábban a három gomb kiszorította a nevet, és a résztvevők neve nem látszott (csak a gombok).
      const row = el("div", { class: "user-row user-manage-row", "data-user-uid": user.uid });
      if (emailNyitva) {
        const jelolo = el("input", { type: "checkbox", class: "user-email-cb", "data-email-uid": user.uid, "aria-label": user.name || user.displayName || user.email || user.uid, disabled: busy, onchange: (e) => handlers.onEmailSelect(user.uid, e.target.checked) });
        jelolo.checked = emailKijelolt.has(user.uid);
        row.appendChild(jelolo);
      }

      const textBox = el("div", { class: "user-row-text" });
      textBox.appendChild(
        el("div", { class: "primary", text: user.name || user.displayName || user.email || user.uid })
      );
      // v210: az e-mail-cím saját, hangsúlyos sorban áll (a tag azonosítója); a szerep és a felvétel ideje alatta.
      if (user.email && user.email !== (user.name || user.displayName)) textBox.appendChild(el("div", { class: "user-email", text: user.email }));
      const sub = [];
      const roleLabelKey =
        user.role === "admin" ? "admin.roleAdmin" : user.role === "topic_lead" ? "admin.roleTopicLead" : "admin.roleMember";
      sub.push(t(uiLang, roleLabelKey));
      sub.push(felvetelSzoveg(uiLang, user));
      textBox.appendChild(el("div", { class: "sub", text: sub.join(" · ") }));
      row.appendChild(textBox);

      const sajatMagam = user.uid === model.myUid;
      const admin = user.role === "admin";
      const topicLead = user.role === "topic_lead";
      // Adminisztrátori szerepkör adása/elvétele csak a fix adminisztrátoré;
      // a Témafelelős kinevezése/visszavonása bármelyik adminé (2026-09-30,
      // tagi kérésre: "Csak admin tud témafelelőst kinevezni és kinevezést
      // elvenni"). A szerver ugyanezt kényszeríti ki (handleSetUserRole);
      // itt csak a felajánlott gombokat igazítjuk hozzá.
      const modosithato = !sajatMagam && user.root !== true;
      const adminSzerepModosithato = model.callerIsRoot === true;

      const actions = el("div", { class: "row-actions" });
      const roleButton = (labelKey, ujSzerep) =>
        el("button", {
          type: "button",
          class: "neutral-btn",
          text: t(uiLang, labelKey),
          disabled: busy,
          onclick: () => handlers.onSetRole(user, ujSzerep),
        });
      if (modosithato) {
        if (admin) {
          if (adminSzerepModosithato) actions.appendChild(roleButton("admin.makeMember", "member"));
        } else if (topicLead) {
          actions.appendChild(roleButton("admin.makeMember", "member"));
          if (adminSzerepModosithato) actions.appendChild(roleButton("admin.makeAdmin", "admin"));
        } else {
          actions.appendChild(roleButton("admin.makeTopicLead", "topic_lead"));
          if (adminSzerepModosithato) actions.appendChild(roleButton("admin.makeAdmin", "admin"));
        }
      }
      // v208: szobatagság beállítása résztvevőnként (a kulcs adminok minden szobának tagjai: nekik nincs).
      const tagsagSzerk = model.userRoomsEdit && model.userRoomsEdit.uid === user.uid ? model.userRoomsEdit : null;
      if (user.root !== true) {
        actions.appendChild(
          el("button", {
            type: "button",
            class: "neutral-btn",
            "data-user-rooms-btn": user.uid,
            "aria-expanded": tagsagSzerk ? "true" : "false",
            text: t(uiLang, "admin.userRoomsBtn"),
            disabled: busy,
            onclick: () => handlers.onUserRoomsOpen(user),
          })
        );
      }
      if (!admin && !sajatMagam) {
        actions.appendChild(
          el("button", {
            type: "button",
            class: "danger-btn",
            text: t(uiLang, "admin.remove"),
            disabled: busy,
            onclick: () => handlers.onRemoveUser(user),
          })
        );
      }
      if (actions.childNodes.length > 0) {
        row.appendChild(actions);
      } else {
        row.appendChild(
          el("span", {
            class: "user-row-note",
            text: sajatMagam ? "\u2014" : t(uiLang, "admin.roleAdmin"),
          })
        );
      }

      userListBox.appendChild(row);

      if (tagsagSzerk) {
        const kijelolt = new Set(tagsagSzerk.selected || []);
        const doboz = el("div", { class: "user-rooms-edit", "data-user-rooms-edit": user.uid });
        const nev = user.name || user.displayName || user.email || user.uid;
        doboz.appendChild(el("h4", { text: t(uiLang, "admin.userRoomsTitle", { name: nev }) }));
        doboz.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.userRoomsHint") }));
        const szobak = (rooms || []).filter((r) => r.archived !== true || kijelolt.has(r.roomId));
        if (!rooms) {
          doboz.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
        } else {
          const lista = el("div", { class: "new-user-rooms" });
          const sor = (room) => {
            const cb = el("input", { type: "checkbox", class: "user-room-cb", "data-room-id": room.roomId });
            cb.checked = kijelolt.has(room.roomId);
            cb.addEventListener("change", () => handlers.onUserRoomsToggle(room.roomId, cb.checked));
            return el("label", { class: "new-user-room" }, [cb, el("span", { text: getRoomDisplayName(room, model.contentLang || uiLang) })]);
          };
          // Elöl az önkéntes-szobák (Audit, Tesztelők, Pénzügyi támogatók), elválasztóval a többitől (mint az új résztvevő űrlapon).
          const elol = szobak.filter((r) => onkentesSzobaSzerep(r.name));
          const sorrend = { audit: 0, tester: 1, admin: 2 };
          elol.sort((a, b) => sorrend[onkentesSzobaSzerep(a.name)] - sorrend[onkentesSzobaSzerep(b.name)]);
          for (const room of elol) lista.appendChild(sor(room));
          if (elol.length) lista.appendChild(el("hr", { class: "new-user-rooms-sep" }));
          for (const room of szobak) if (!onkentesSzobaSzerep(room.name)) lista.appendChild(sor(room));
          doboz.appendChild(lista);
        }
        doboz.appendChild(el("p", { class: "hint", "data-user-rooms-count": "1", text: t(uiLang, "admin.userRoomsCount", { n: kijelolt.size }) }));
        const gombSor = el("div", { class: "row-actions" });
        gombSor.appendChild(
          el("button", {
            type: "button",
            class: "primary-btn compact",
            "data-user-rooms-save": "1",
            text: t(uiLang, "admin.userRoomsSave"),
            disabled: busy || !rooms,
            onclick: () => handlers.onUserRoomsSave(user),
          })
        );
        gombSor.appendChild(
          el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "admin.cancel"), disabled: busy, onclick: () => handlers.onUserRoomsOpen(user) })
        );
        doboz.appendChild(gombSor);
        userListBox.appendChild(doboz);
      }
    }
  }
  usersSection.appendChild(userListBox);
  parts.users.push(usersSection);

  // --- Tagság kezelése
  const memberSection = el("section", { class: "panel-section" });
  memberSection.appendChild(el("h3", { text: t(uiLang, "admin.roomMembers") }));

  if (!rooms) {
    memberSection.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
  } else if (rooms.length === 0) {
    memberSection.appendChild(el("p", { class: "hint", text: t(uiLang, "chat.noRoomsAdmin") }));
  } else {
    const select = el("select", { class: "panel-input" });
    // A tagok szobaválasztója is fa-sorrendben, behúzva (2026-10-04, tagi
    // kérésre: a hierarchia MINDENHOL látsszon) - korábban ez maradt lapos.
    for (const { room, melyseg } of szobaFaSorrend(rooms)) {
      const opt = el("option", {
        value: room.roomId,
        text: szobaOpcioFelirat(melyseg, getRoomDisplayName(room, model.contentLang || uiLang, uiLang)),
      });
      if (room.roomId === selectedRoomId) opt.selected = true;
      select.appendChild(opt);
    }
    select.addEventListener("change", () => handlers.onSelectRoom(select.value));
    memberSection.appendChild(select);

    // Egy kattintással mindenki (aktív résztvevő) felvétele / mindenki eltávolítása; a két kulcs admin mindig tag marad
    // (a szerver is visszateszi). A "senki" kérdésre rákérdez (v122).
    if (handlers.onSetAllMembers) {
      memberSection.appendChild(el("div", { class: "panel-row members-bulk" }, [
        el("button", { type: "button", class: "neutral-btn compact", text: t(uiLang, "admin.membersAll"), disabled: busy || !users, onclick: () => handlers.onSetAllMembers(true) }),
        el("button", { type: "button", class: "neutral-btn compact", text: t(uiLang, "admin.membersNone"), disabled: busy || !users, onclick: () => handlers.onSetAllMembers(false) }),
      ]));
    }

    const selectedRoom = rooms.find((r) => r.roomId === selectedRoomId);
    // Halmazban: a bejelöltség eldöntése tagonként egy lépés, nem a teljes
    // taglista végigolvasása (a listát MINDEN rendernél újraépítjük).
    const currentMembers = new Set(selectedRoom ? selectedRoom.memberIds : []);

    const userList = el("div", { class: "panel-list" });
    // A törölt (eltávolított) tag nem látszik: a régi módon inaktívvá tett
    // tagok sem jelennek meg itt (2026-09-29). A szűrés EGYSZER fut - korábban
    // ugyanez a lista kétszer épült fel minden rendernél.
    // A hívó (te) legelöl, "te" jelöléssel, a többi kulcs admin utána: így mindig megtalálható, és látszik, hogy
    // a kulcs adminok minden szobának tagjai (2026-10-10, tagi kérés). A többi tag az eredeti sorrendben marad.
    const aktivTagok = users
      ? users.filter((u) => u.active).sort((a, b) => (b.uid === model.currentUid) - (a.uid === model.currentUid) || (b.root === true) - (a.root === true))
      : [];
    // Figyelmeztetés, ha a szobának nincs tagja (a kulcs adminon kívül is): senki nem látja, és nem fordul le.
    const nincsTag = selectedRoom && !(selectedRoom.memberIds || []).length;
    if (nincsTag) memberSection.appendChild(el("div", { class: "panel-status err", text: t(uiLang, "admin.roomNoMembers") }));
    if (!users) {
      userList.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
    } else if (aktivTagok.length === 0) {
      userList.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.noUsers") }));
    } else {
      for (const user of aktivTagok) {
        const row = el("label", { class: "panel-check-row" });
        // A kulcs admin minden szobának tagja: bejelölt és zárolt (a szerver is mindig visszateszi).
        const kulcs = user.root === true;
        const cb = el("input", {
          type: "checkbox",
          checked: kulcs || currentMembers.has(user.uid),
          disabled: busy || kulcs,
        });
        cb.addEventListener("change", () => handlers.onToggleMember(user.uid, cb.checked));
        row.appendChild(cb);

        const primary = user.name || user.displayName || user.email || user.uid;
        const secondaryParts = [];
        // A szerepkör HÁROM értékű (2026-10-01, kódaudit): a témafelelős
        // korábban itt "tag"-ként jelent meg, ami félrevezető volt - az
        // adminnak épp azt kell látnia, kinek van szobakezelési joga.
        secondaryParts.push(
          t(uiLang, user.role === "admin" ? "admin.roleAdmin" : user.role === "topic_lead" ? "admin.roleTopicLead" : "admin.roleMember")
        );
        secondaryParts.push(felvetelSzoveg(uiLang, user));
        if (kulcs) secondaryParts.push(t(uiLang, "admin.rootMember"));
        if (!user.active) secondaryParts.push(t(uiLang, "admin.inactive"));

        const textBox = el("div", { class: "panel-check-text" });
        textBox.appendChild(el("div", { class: "primary", text: user.uid === model.currentUid ? `${primary} (${t(uiLang, "admin.you")})` : primary }));
        if (user.email && user.email !== primary) textBox.appendChild(el("div", { class: "user-email", text: user.email }));
        textBox.appendChild(el("div", { class: "sub", text: secondaryParts.join(" · ") }));
        row.appendChild(textBox);
        userList.appendChild(row);
      }
    }
    memberSection.appendChild(userList);
  }
  parts.members.push(memberSection);

  // Témák kezelése (2026-10-19, csak admin): átnevezés, áthelyezés, törlés, zárolás, csak olvasható.
  if (!isTopicLeadCaller) parts.topics.push(renderTemakKezelese(model, handlers));

  // Közlemények (2026-10-25, csak admin): küldés és a tudomásul vétel követése.
  if (!isTopicLeadCaller) parts.announcements.push(renderKozlemenyek({ ...model, myUid: model.myUid, contentLang: model.contentLang }, handlers));

  // Kezdőlap (2026-10-25, csak admin): a tagok kezdőlapján megjelenő szobák.
  if (!isTopicLeadCaller) parts.home.push(renderKezdolapBeallitas(model, handlers));

  // Tagsági kártya előnézete (v165, csak admin; kitalált adatok, nincs szerverhívás).
  if (!isTopicLeadCaller) parts.card.push(renderKartyaElonezet(model, handlers));

  // Önkéntes jelentkezések (v192, csak admin): szervezői lista, részletek, belső megjegyzés, kérdés, döntés.
  if (!isTopicLeadCaller) parts.applications.push(renderJelentkezesekAdmin(model, handlers));

  parts.usage.push(renderUsageSection(uiLang, model.usage));

  // --- Főmenü, egy csoport almenüi (Szobák kezelése) vagy a kiválasztott oldal
  const menuGomb = (m) => el("button", { type: "button", class: "list-item admin-menu-item", text: t(uiLang, m.labelKey), onclick: () => handlers.onOpenMenu(m.key) });
  const vissza = (felirat, kezelo) => el("button", { type: "button", class: "admin-back", text: "‹ " + felirat, onclick: kezelo });
  if (menu === null) {
    const lista = el("div", { class: "admin-menu" });
    for (const m of visibleMenus) lista.appendChild(menuGomb(m));
    sheet.appendChild(lista);
  } else if (ADMIN_SUBMENUS[menu]) {
    // Csoport-oldal: az almenük gombjai.
    sheet.appendChild(vissza(t(uiLang, "admin.title"), handlers.onBackToMenu));
    sheet.appendChild(el("h3", { class: "admin-group-title", text: t(uiLang, ADMIN_MENUS.find((m) => m.key === menu).labelKey) }));
    const lista = el("div", { class: "admin-menu" });
    for (const g of ADMIN_SUBMENUS[menu]) lista.appendChild(menuGomb(g));
    sheet.appendChild(lista);
  } else {
    // Oldal: a gyerek-oldalról a csoportjába lépünk vissza, a főmenü-szintűről a főmenübe.
    const szulo = adminSzuloMenu(menu);
    if (szulo) {
      sheet.appendChild(vissza(t(uiLang, ADMIN_MENUS.find((m) => m.key === szulo).labelKey), () => handlers.onOpenMenu(szulo)));
    } else {
      sheet.appendChild(vissza(t(uiLang, "admin.title"), handlers.onBackToMenu));
    }
    for (const part of parts[menu]) sheet.appendChild(part);
  }

  if (statusKey) sheet.appendChild(el("div", { class: "panel-status ok", text: t(uiLang, statusKey, model.statusParams || undefined) }));
  if (errorKey && !(model.reauth && (errorKey === "admin.reauthNeeded" || errorKey === "admin.reauthFailed"))) sheet.appendChild(el("div", { class: "panel-status err", text: t(uiLang, errorKey) + (model.errorText ? " (" + model.errorText + ")" : "") }));

  sheet.appendChild(
    el("button", { type: "button", class: "text-btn sheet-close", text: t(uiLang, "admin.close"), onclick: handlers.onClose })
  );
}

/** USD-összeg a felület nyelvének számformátumával; kis összegnél 4 tizedes. */
function usdFormat(uiLang, x) {
  const v = Number(x) || 0;
  try {
    return new Intl.NumberFormat(localeFor(uiLang), {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: v !== 0 && Math.abs(v) < 1 ? 4 : 2,
    }).format(v);
  } catch (e) {
    return "$" + v.toFixed(2);
  }
}

function szamFormat(uiLang, n) {
  try {
    return new Intl.NumberFormat(localeFor(uiLang)).format(Number(n) || 0);
  } catch (e) {
    return String(n);
  }
}

/**
 * A Gemini-fordítás becsült költsége (az adminAction "usageSummary"
 * válaszából): mai költség a napi plafonhoz képest, 30 napos összeg,
 * havi előrejelzés, napi oszlopdiagram és a legtöbb költséget okozó tagok.
 * A számok BECSLÉSEK a tokenszámból - a tényleges számla a Google Cloud
 * Billingben van, ezt a szakasz alja ki is írja.
 */
function renderUsageSection(uiLang, usage) {
  const section = el("section", { class: "panel-section usage-section" });
  section.appendChild(el("h3", { text: t(uiLang, "admin.usageTitle") }));

  if (usage === null || usage === undefined) {
    section.appendChild(el("div", { class: "usage-line sub", text: t(uiLang, "admin.usageLoading") }));
    return section;
  }
  if (usage === false) {
    section.appendChild(el("div", { class: "panel-status err", text: t(uiLang, "admin.loadFailed") }));
    return section;
  }

  const cap = Number(usage.dailyCapUsd) || 0;
  const ma = Number(usage.todayCostUsd) || 0;
  section.appendChild(el("div", {
    class: "usage-line usage-today",
    text: t(uiLang, "admin.usageToday", {
      cost: usdFormat(uiLang, ma),
      cap: cap > 0 ? usdFormat(uiLang, cap) : t(uiLang, "admin.usageNoCap"),
    }),
  }));
  if (cap > 0) {
    const arany = Math.min(ma / cap, 1);
    const sav = el("div", { class: "usage-meter" + (arany >= 1 ? " full" : arany >= 0.8 ? " warn" : "") });
    const kitoltes = el("div", { class: "usage-meter-fill" });
    kitoltes.style.width = `${Math.round(arany * 100)}%`;
    sav.appendChild(kitoltes);
    section.appendChild(sav);
    if (ma >= cap) {
      section.appendChild(el("div", { class: "panel-status err", text: t(uiLang, "admin.usageCapReached") }));
    }
  }

  section.appendChild(el("div", {
    class: "usage-line",
    text: t(uiLang, "admin.usagePeriod", {
      cost: usdFormat(uiLang, usage.totalCostUsd),
      messages: szamFormat(uiLang, usage.totalMessages),
      calls: szamFormat(uiLang, usage.totalCalls),
    }),
  }));
  section.appendChild(el("div", {
    class: "usage-line",
    text: t(uiLang, "admin.usageProjection", { cost: usdFormat(uiLang, usage.monthlyProjectionUsd) }),
  }));
  // E-mail összefoglaló feliratkozói (2026-10-15): a Brevo ingyenes kerete napi 300 levél.
  const es = usage.emailSubscribers;
  if (es && (Number.isFinite(es.daily) || Number.isFinite(es.weekly))) {
    const napi = Number(es.daily) || 0;
    const heti = Number(es.weekly) || 0;
    section.appendChild(el("div", {
      class: "usage-line usage-email",
      text: t(uiLang, "admin.emailSubscribers", {
        total: szamFormat(uiLang, napi + heti), daily: szamFormat(uiLang, napi), weekly: szamFormat(uiLang, heti),
      }),
    }));
    if (es.enabled !== true) {
      section.appendChild(el("div", { class: "usage-line sub usage-email-off", text: t(uiLang, "admin.emailDigestOff") }));
    }
  }
  // Cél szerinti bontás (2026-10-03): a képjavítás (@1) költsége is külön
  // látszik - a fenti összegek MÁR tartalmazzák, ez csak a megoszlás.
  if (usage.byPurpose && typeof usage.byPurpose === "object") {
    const bp = usage.byPurpose;
    section.appendChild(el("div", {
      class: "usage-line sub usage-purposes",
      text: t(uiLang, "admin.usageByPurpose", {
        translation: usdFormat(uiLang, Number(bp.translation) || 0),
        names: usdFormat(uiLang, Number(bp.nameSuggest) || 0),
        images: usdFormat(uiLang, Number(bp.imageEnhance) || 0),
        ocr: usdFormat(uiLang, Number(bp.ocr) || 0),
      }),
    }));
  }
  if (usage.model) {
    section.appendChild(el("div", { class: "usage-line sub", text: t(uiLang, "admin.usageModel", { model: usage.model }) }));
  }
  if (usage.imageEnhanceModel) {
    section.appendChild(el("div", {
      class: "usage-line sub usage-image-model",
      text: t(uiLang, "admin.usageImageModel", { model: usage.imageEnhanceModel }),
    }));
  }

  // Napi oszlopdiagram (a legnagyobb nap = teljes magasság).
  const napok = Array.isArray(usage.days) ? usage.days : [];
  const max = napok.reduce((m, d) => Math.max(m, Number(d.costUsd) || 0), 0);
  if (max > 0) {
    const diagram = el("div", { class: "usage-bars", "aria-hidden": "true" });
    for (const d of napok) {
      const oszlop = el("div", {
        class: "usage-bar",
        title: `${d.day}: ${usdFormat(uiLang, d.costUsd)}`,
      });
      oszlop.style.height = `${Math.max(2, Math.round(((Number(d.costUsd) || 0) / max) * 100))}%`;
      diagram.appendChild(oszlop);
    }
    section.appendChild(diagram);
  }

  const top = Array.isArray(usage.topUsers) ? usage.topUsers.filter((u) => u.costUsd > 0) : [];
  if (top.length) {
    section.appendChild(el("div", { class: "panel-label small", text: t(uiLang, "admin.usageTopUsers") }));
    const lista = el("div", { class: "usage-top" });
    for (const u of top) {
      const sor = el("div", { class: "usage-top-row" });
      sor.appendChild(el("span", { class: "usage-top-name", text: u.name || u.uid }));
      sor.appendChild(el("span", { class: "usage-top-cost", text: usdFormat(uiLang, u.costUsd) }));
      lista.appendChild(sor);
    }
    section.appendChild(lista);
  }

  if (Number(usage.unpricedCalls) > 0) {
    section.appendChild(el("div", {
      class: "panel-status err",
      text: t(uiLang, "admin.usageUnpriced", { count: szamFormat(uiLang, usage.unpricedCalls) }),
    }));
  }
  section.appendChild(el("div", { class: "usage-note", text: t(uiLang, "admin.usageNote") }));
  return section;
}

/**
 * Profilszerkesztő: megjelenített név, felület nyelve és csevegés nyelve.
 */
export function renderProfilePanel(app, model, handlers) {
  const {
    uiLang, name, nameLatn, nameJa, nameZh, nameKo, nameCyr, nameEl,
    contentLang, contentLangs, uiLangs, busy, statusKey, errorKey, suggesting, nameLangKerdes,
  } = model;
  const sheet = sheetShell(app, uiLang, t(uiLang, "profile.title"), handlers.onClose);

  // A saját tagsági kártya (v166): csak ha a tagnak már van kiadott száma; az adatot a szerver adja (állapotot is).
  if (model.membershipCard) {
    const k = model.membershipCard;
    sheet.appendChild(renderTagsagiKartya({ uiLang, nickname: k.nickname, number: k.number, joinedYear: k.joinedYear, joinedMonth: k.joinedMonth, status: k.status }));
  }

  // Megjelenített név
  sheet.appendChild(el("label", { class: "panel-label", text: t(uiLang, "profile.name") }));
  const nameInput = el("input", { type: "text", class: "panel-input", maxlength: "80" });
  nameInput.value = name || "";
  nameInput.addEventListener("input", () => handlers.onNameChange(nameInput.value));
  sheet.appendChild(nameInput);

  // --- A név írásmód-változatai (2026-09-30, tagi kérésre hat írásrendszerre
  // bővítve: latin, japán, kínai, koreai, cirill, görög).
  //
  // Miért kézzel megadható és nem automatikus? Egy kandzsival írt névnek
  // több érvényes olvasata van (山崎 = Yamazaki VAGY Yamasaki), és nincs
  // szabály, amiből eldőlne, melyik a helyes. A "Javaslat" gomb (2026-10-04
  // óta MIND A HAT írásmódra) kitölti az üres mezőket, de az utolsó szó a név
  // gazdájáé: átnézi, javítja, és ő menti.
  // ÖSSZECSUKVA (v174, a titkár #5 javaslata): a más írásrendszerű névváltozatok csak annak látszanak, aki megnyitja ("Név más írásrendszerekkel");
  // a mindennapi felület a megjelenített névvel kezdődik. Nyitva marad újrarajzoláskor és ha a nyelv-kérdés vagy egy kitöltött változat van.
  const nevReszlet = el("details", { class: "profile-name-variants" });
  nevReszlet.open = nevValtozatokNyitva || nameLangKerdes === true;
  nevReszlet.addEventListener("toggle", () => { nevValtozatokNyitva = nevReszlet.open; });
  nevReszlet.appendChild(el("summary", { class: "profile-name-variants-summary", text: t(uiLang, "profile.nameVariantsTitle") }));
  sheet.appendChild(nevReszlet);
  const nevMezo = (labelKey, ertek, onChange) => {
    nevReszlet.appendChild(el("label", { class: "panel-label", text: t(uiLang, labelKey) }));
    const input = el("input", { type: "text", class: "panel-input", maxlength: "80" });
    input.value = ertek || "";
    input.addEventListener("input", () => onChange(input.value));
    nevReszlet.appendChild(input);
  };
  nevMezo("profile.nameLatn", nameLatn, handlers.onNameLatnChange);
  nevMezo("profile.nameJa", nameJa, handlers.onNameJaChange);
  nevMezo("profile.nameZh", nameZh, handlers.onNameZhChange);
  nevMezo("profile.nameKo", nameKo, handlers.onNameKoChange);
  nevMezo("profile.nameCyr", nameCyr, handlers.onNameCyrChange);
  nevMezo("profile.nameEl", nameEl, handlers.onNameElChange);

  nevReszlet.appendChild(el("div", { class: "hint", text: t(uiLang, "profile.nameVariantHint") }));

  // Ha a név csak han írásjegyekből áll (kandzsi/hanzi/handzsa), NEM
  // tippelünk: a három nyelv átírása teljesen különböző. Megkérdezzük.
  if (nameLangKerdes) {
    nevReszlet.appendChild(el("div", { class: "panel-status ask", text: t(uiLang, "profile.nameLangAsk") }));
    const valaszto = el("div", { class: "panel-row lang-pick" });
    for (const [kod, kulcs] of [["ja", "profile.nameLangJa"], ["zh", "profile.nameLangZh"], ["ko", "profile.nameLangKo"]]) {
      valaszto.appendChild(
        el("button", {
          type: "button",
          class: "neutral-btn",
          text: t(uiLang, kulcs),
          disabled: busy || suggesting === true,
          onclick: () => handlers.onSuggestNames(kod),
        })
      );
    }
    nevReszlet.appendChild(valaszto);
  }

  const suggestRow = el("div", { class: "panel-row" });
  suggestRow.appendChild(
    el("button", {
      type: "button",
      class: "neutral-btn",
      text: suggesting ? "⏳ " + t(uiLang, "profile.suggesting") : "✨ " + t(uiLang, "profile.suggestNames"),
      disabled: busy || suggesting === true,
      onclick: handlers.onSuggestNames,
    })
  );
  nevReszlet.appendChild(suggestRow);

  // Felület nyelve (legördülő)
  sheet.appendChild(el("label", { class: "panel-label", text: t(uiLang, "profile.uiLang") }));
  const uiLangSelect = el("select", { class: "panel-input" });
  uiLangSelect.appendChild(el("option", { value: "", text: t(uiLang, "profile.selectLang"), disabled: "true" }));
  for (const [code, label] of Object.entries(uiLangs || {})) {
    const langName = typeof label === "object" && label !== null ? (label.name || code) : label;
    const opt = el("option", { value: code, text: langName });
    if (code === uiLang) opt.selected = true;
    uiLangSelect.appendChild(opt);
  }
  uiLangSelect.addEventListener("change", () => handlers.onUiLangChange(uiLangSelect.value));
  sheet.appendChild(uiLangSelect);

  // Csevegés nyelve (legördülő)
  sheet.appendChild(el("label", { class: "panel-label", text: t(uiLang, "profile.chatLang") }));
  const codes = Object.keys(contentLangs || {});
  if (codes.length === 0) {
    sheet.appendChild(el("div", { class: "panel-status err", text: t(uiLang, "profile.noLanguages") }));
  } else {
    const chatLangSelect = el("select", { class: "panel-input" });
    for (const [code, label] of Object.entries(contentLangs)) {
      const langName = typeof label === "object" && label !== null ? (label.name || code) : label;
      const opt = el("option", { value: code, text: langName });
      if (code === contentLang) opt.selected = true;
      chatLangSelect.appendChild(opt);
    }
    chatLangSelect.addEventListener("change", () => handlers.onContentLangChange(chatLangSelect.value));
    sheet.appendChild(chatLangSelect);
  }

  sheet.appendChild(
    el("button", {
      type: "button",
      class: "primary-btn",
      text: t(uiLang, "profile.save"),
      disabled: busy,
      onclick: handlers.onSave,
    })
  );

  if (statusKey) sheet.appendChild(el("div", { class: "panel-status ok", text: t(uiLang, statusKey) }));
  if (errorKey) sheet.appendChild(el("div", { class: "panel-status err", text: t(uiLang, errorKey) }));

  // --- Ez az eszköz és a belépés (v182, "emlékezz erre az eszközre", 1. szint): tartós tároló, és mikor veszett el utoljára a belépés.
  // Csak saját eszközön (a közösön szándékosan nem marad meg semmi). Tájékoztató sor, nincs kapcsoló.
  if (model.eszkozInfo) {
    const info = model.eszkozInfo;
    const eszkoz = el("div", { class: "panel-section profile-device" });
    eszkoz.appendChild(el("label", { class: "panel-label", text: t(uiLang, "profile.deviceInfoTitle") }));
    if (info.tartos === "igen") eszkoz.appendChild(el("div", { class: "hint profile-device-persist", text: t(uiLang, "profile.devicePersistYes") }));
    else if (info.tartos === "nem") eszkoz.appendChild(el("div", { class: "hint profile-device-persist", text: t(uiLang, "profile.devicePersistNo") }));
    const datum = info.elveszett ? new Date(info.elveszett.at).toLocaleString(localeFor(uiLang)) : null;
    eszkoz.appendChild(el("div", { class: "hint profile-device-lost", text: datum ? t(uiLang, "profile.deviceLost", { date: datum }) : t(uiLang, "profile.deviceLostNone") }));
    sheet.appendChild(eszkoz);
  }

  // --- A saját naplózott névváltozásaim (D-010, v185): csak ha a napló be van kapcsolva (alapból ki); a szerver csak a SAJÁT előzményt adja.
  if (model.nevNaplo && model.nevNaplo.enabled === true) {
    const naplo = el("div", { class: "panel-section profile-name-log" });
    naplo.appendChild(el("label", { class: "panel-label", text: t(uiLang, "nameLog.title") }));
    naplo.appendChild(renderNevNaplo(uiLang, model.nevNaplo.entries));
    sheet.appendChild(naplo);
  }

  // --- Push-értesítés ezen az eszközön (2026-10-16, tagi kérés: ki/be kapcsoló a profilban). Eszközönként külön: a kapcsoló
  // csak azt a böngészőt / telepített alkalmazást érinti, ahol áll; azonnal érvényes (saját állapot- és hibasorral, nem a "Mentés"
  // gombbal). Ha az eszköz nem támogatja (pl. iPhone-on a böngészőfül), vagy a böngészőben le van tiltva, a kapcsoló nem használható.
  if (handlers.onPushToggle && model.push) {
    const pushSzakasz = el("div", { class: "panel-section profile-push" });
    pushSzakasz.appendChild(el("label", { class: "panel-label", text: t(uiLang, "profile.pushTitle") }));
    if (!model.push.tamogatott) {
      pushSzakasz.appendChild(el("div", { class: "hint", text: t(uiLang, "profile.pushUnsupported") }));
    } else {
      const letiltott = model.push.engedely === "denied";
      const jelolo = el("input", { type: "checkbox", id: "profile-push", disabled: model.pushBusy === true || letiltott });
      jelolo.checked = model.push.be === true;
      jelolo.addEventListener("change", () => handlers.onPushToggle(jelolo.checked));
      const sor = el("label", { class: "panel-label small readonly-check", for: "profile-push" });
      sor.appendChild(jelolo);
      sor.appendChild(document.createTextNode(" " + t(uiLang, "profile.pushLabel")));
      pushSzakasz.appendChild(sor);
      if (letiltott) pushSzakasz.appendChild(el("div", { class: "panel-status err", text: t(uiLang, "notif.blocked") }));
      pushSzakasz.appendChild(el("div", { class: "hint", text: t(uiLang, "profile.pushHint") }));
    }
    if (model.pushStatusKey) pushSzakasz.appendChild(el("div", { class: "panel-status ok", text: t(uiLang, model.pushStatusKey) }));
    if (model.pushErrorKey) pushSzakasz.appendChild(el("div", { class: "panel-status err", text: t(uiLang, model.pushErrorKey) }));
    sheet.appendChild(pushSzakasz);
  }

  // --- E-mail összefoglaló (2026-10-16, tagi kérés: a profilban legyen "napi összefoglalót kérek a történtekről").
  // Külön szakasz a "Mentés" gomb UTÁN, mert a választás AZONNAL mentődik (saját állapot- és hibasorral), nem a gombbal -
  // a két mentési mód így nem keveredik. A szerver napi egyszer, reggel küldi: minden új hozzászólás, új téma és új szoba.
  if (handlers.onEmailFrequency) {
    // div (nem section): a .panel-section:first-of-type szabály az egyetlen section-ről levenné az elválasztó vonalat.
    const emailSzakasz = el("div", { class: "panel-section profile-email" });
    emailSzakasz.appendChild(el("label", { class: "panel-label", text: t(uiLang, "forum.emailLabel") }));
    const toltodik = model.emailFrequency === null || model.emailFrequency === undefined;
    const emailValaszto = el("select", { class: "panel-input profile-email-select", disabled: toltodik });
    if (toltodik) {
      emailValaszto.appendChild(el("option", { value: "", text: t(uiLang, "common.loading") }));
    } else {
      for (const f of ["off", "daily", "weekly"]) {
        const o = el("option", { value: f, text: t(uiLang, `profile.email_${f}`) });
        if (model.emailFrequency === f) o.selected = true;
        emailValaszto.appendChild(o);
      }
      emailValaszto.addEventListener("change", () => handlers.onEmailFrequency(emailValaszto.value));
    }
    emailSzakasz.appendChild(emailValaszto);
    emailSzakasz.appendChild(el("div", { class: "hint", text: t(uiLang, "profile.emailHint") }));
    if (model.emailStatusKey) emailSzakasz.appendChild(el("div", { class: "panel-status ok", text: t(uiLang, model.emailStatusKey) }));
    if (model.emailErrorKey) emailSzakasz.appendChild(el("div", { class: "panel-status err", text: t(uiLang, model.emailErrorKey) }));
    sheet.appendChild(emailSzakasz);
  }

  sheet.appendChild(
    el("button", { type: "button", class: "text-btn sheet-close", text: t(uiLang, "admin.close"), onclick: handlers.onClose })
  );
  // A futó verzió a profil LEGALJÁN (2026-10-08, tagi kérésre: a DEV sávról ide került). Korábban (v40)
  // tévesen az ADMIN panel aljára került, és a tesztem csak a forrásszöveget nézte - ezért nem derült ki.
  const verzio = beepitettVerzio();
  const kiadas = beepitettKiadas();
  // A verziósor a futó ÜZEMMÓDOT is mutatja (2026-10-19, a titkár #46 kérése: "Fejlesztési / Normál üzemmód"): így két ember ugyanazt a változatot nézi-e, azonnal látszik.
  const uzemmod = t(uiLang, ENV.name === "prod" ? "profile.envProd" : "profile.envDev");
  if (verzio) sheet.appendChild(el("div", { class: "hint version-info", text: t(uiLang, "profile.version", { v: `${kiadas ? `${kiadas} · ${verzio}` : verzio} · ${uzemmod}` }) }));
}
